import { config } from '@ps/config';
import 'client-only';

declare global {
  interface Window {
    grecaptcha?: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (
          siteKey: string,
          options: { action: string },
        ) => Promise<string>;
      };
    };
  }
}

type RecaptchaEnterprise = NonNullable<Window['grecaptcha']>['enterprise'];

const RECAPTCHA_LOAD_TIMEOUT_MS = 10_000;
const RECAPTCHA_POLL_MS = 100;
const RECAPTCHA_SRC_MATCH = 'recaptcha/enterprise.js';

/**
 * Ensure the reCAPTCHA Enterprise script is loading. The root layout injects it with
 * `next/script` `lazyOnload` (browser-idle) to keep it off form-less pages' critical
 * path — but a caller can need a token BEFORE idle (e.g. the express checkout mints a
 * guest the instant the buyer taps Apple Pay on a bio page, long before the bio goes
 * idle). Rather than waiting on idle, inject the script on demand. Idempotent: skips
 * if the script (from either loader) is already present.
 */
function ensureRecaptchaScriptLoading(): void {
  if (typeof document === 'undefined' || window.grecaptcha?.enterprise) {
    return;
  }
  if (document.querySelector(`script[src*="${RECAPTCHA_SRC_MATCH}"]`)) {
    return;
  }
  const script = document.createElement('script');
  script.src = `https://www.google.com/recaptcha/enterprise.js?render=${config.recaptchaV3SiteKey}`;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
}

/**
 * Resolve once the reCAPTCHA Enterprise script is available — ensuring it is actually
 * loading first (see {@link ensureRecaptchaScriptLoading}), then polling briefly since
 * a caller can fire before it finishes. Rejects if it never loads in time.
 */
function waitForRecaptchaEnterprise(): Promise<RecaptchaEnterprise> {
  return new Promise((resolve, reject) => {
    if (window.grecaptcha?.enterprise) {
      resolve(window.grecaptcha.enterprise);
      return;
    }
    ensureRecaptchaScriptLoading();
    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (window.grecaptcha?.enterprise) {
        clearInterval(timer);
        resolve(window.grecaptcha.enterprise);
      } else if (Date.now() - startedAt >= RECAPTCHA_LOAD_TIMEOUT_MS) {
        clearInterval(timer);
        reject(new Error('reCAPTCHA Enterprise not loaded'));
      }
    }, RECAPTCHA_POLL_MS);
  });
}

/**
 * Execute reCAPTCHA v3 Enterprise and return a token, waiting for the script to load.
 */
export async function getRecaptchaToken(action: string): Promise<string> {
  const enterprise = await waitForRecaptchaEnterprise();
  return new Promise((resolve, reject) => {
    enterprise.ready(async () => {
      try {
        const token = await enterprise.execute(config.recaptchaV3SiteKey, {
          action,
        });
        resolve(token);
      } catch (error) {
        reject(error);
      }
    });
  });
}
